import ZuttleLogo from './components/ZuttleLogo';
import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Building2, Users, Car, DollarSign, Shield, LogOut, Menu, X, FileText, Bus, ClipboardList, CreditCard } from 'lucide-react';
import LoginPage from './components/auth/LoginPage';
import AdminDashboard from './components/dashboard/AdminDashboard';
import CompanyList from './components/companies/CompanyList';
import CompanyDetail from './components/companies/CompanyDetail';
import DriverList from './components/drivers/DriverList';
import VehicleList from './components/vehicles/VehicleList';
import PayoutDashboard from './components/payouts/PayoutDashboard';
import AdminUserList from './components/admin-users/AdminUserList';
import DocumentQueue from './components/charter/DocumentQueue';
import HireList from './components/charter/HireList';
import WaitlistPage from './components/waitlist/WaitlistPage';
import PaymentsPage from './components/payments/PaymentsPage';
import { AdminAuthAPI, AdminCharterAPI, APIUtils } from './services/api';

const App = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [authState, setAuthState] = useState('loading');
  const [admin, setAdmin] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // The charter client flag (spec 5.7, R77). The charter pages show when
  // `enabled || hasHires`; until it loads, or if it cannot be read, they stay
  // hidden and the portal is exactly as before.
  const [charterFlag, setCharterFlag] = useState({ enabled: false, hasHires: false });

  const navigateToCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    setCurrentPage('company-detail');
  };

  const navigateBackToCompanies = () => {
    setSelectedCompanyId(null);
    setCurrentPage('companies');
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (authState !== 'authenticated') return undefined;
    let live = true;
    AdminCharterAPI.getEnabled()
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        if (live) setCharterFlag({ enabled: data.enabled === true, hasHires: data.hasHires === true });
      })
      .catch(() => { if (live) setCharterFlag({ enabled: false, hasHires: false }); });
    return () => { live = false; };
  }, [authState]);

  const charterVisible = charterFlag.enabled || charterFlag.hasHires;

  const checkAuth = async () => {
    const token = localStorage.getItem('zuttle_admin_token');
    const storedAdmin = localStorage.getItem('zuttle_admin_user');

    if (token && storedAdmin) {
      try {
        const response = await AdminAuthAPI.getProfile();
        if (response.data.success) {
          setAdmin(response.data.data);
          setAuthState('authenticated');
        } else {
          throw new Error('Invalid token');
        }
      } catch {
        APIUtils.clearAuth();
        setAuthState('login');
      }
    } else {
      setAuthState('login');
    }
  };

  const handleLogin = (adminData) => {
    setAdmin(adminData);
    setAuthState('authenticated');
  };

  const handleLogout = () => {
    // Server-side logout: invalidates all outstanding tokens (fire-and-forget)
    try { AdminAuthAPI.logout().catch(() => {}); } catch (e) { /* never block local logout */ }

    setAdmin(null);
    setAuthState('login');
    setCurrentPage('dashboard');
    setCharterFlag({ enabled: false, hasHires: false });
    APIUtils.clearAuth();
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'drivers', label: 'Drivers', icon: Users },
    { id: 'vehicles', label: 'Vehicles', icon: Car },
    { id: 'payouts', label: 'Payouts', icon: DollarSign },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'waitlist', label: 'Waitlist', icon: ClipboardList },
  ];

  if (charterVisible) {
    navItems.push({ id: 'charter-documents', label: 'Documents', icon: FileText });
    navItems.push({ id: 'hires', label: 'Hires', icon: Bus });
  }

  // Only show admin users for super_admin
  if (admin?.role === 'super_admin') {
    navItems.push({ id: 'admin-users', label: 'Admin Users', icon: Shield });
  }

  const renderContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return <AdminDashboard onNavigate={setCurrentPage} />;
      case 'companies':
        return <CompanyList onViewCompany={navigateToCompany} />;
      case 'company-detail':
        return <CompanyDetail companyId={selectedCompanyId} onBack={navigateBackToCompanies} charterVisible={charterVisible} isSuperAdmin={admin?.role === 'super_admin'} />;
      case 'drivers':
        return <DriverList />;
      case 'vehicles':
        return <VehicleList />;
      case 'payouts':
        return <PayoutDashboard />;
      case 'payments':
        return <PaymentsPage isSuperAdmin={admin?.role === 'super_admin'} />;
      case 'waitlist':
        return <WaitlistPage />;
      case 'admin-users':
        return <AdminUserList />;
      case 'charter-documents':
        return charterVisible ? <DocumentQueue /> : <AdminDashboard onNavigate={setCurrentPage} />;
      case 'hires':
        return charterVisible
          ? <HireList isSuperAdmin={admin?.role === 'super_admin'} />
          : <AdminDashboard onNavigate={setCurrentPage} />;
      default:
        return <AdminDashboard onNavigate={setCurrentPage} />;
    }
  };

  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (authState !== 'authenticated') {
    return <LoginPage onLogin={handleLogin} />;
  }

  const handleNavClick = (pageId) => {
    setCurrentPage(pageId);
    setSidebarOpen(false);
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center">
          {/* Real logo, reversed for the dark sidebar. */}
          <div>
            <ZuttleLogo variant="white" height={26} />
            <p className="text-xs text-gray-400 mt-1">Admin Portal</p>
          </div>
        </div>
        <button
          className="md:hidden p-1 rounded-lg text-gray-400 hover:bg-gray-800"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`w-full flex items-center px-3 py-2.5 text-left rounded-lg transition-colors text-sm ${
                  (currentPage === item.id || (item.id === 'companies' && currentPage === 'company-detail'))
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <Icon size={18} className="mr-3" />
                {item.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-800">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
            <span className="text-white text-xs font-medium">
              {admin?.name?.substring(0, 2).toUpperCase() || 'AD'}
            </span>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{admin?.name || 'Admin'}</p>
            <p className="text-xs text-gray-400 truncate">{admin?.email || ''}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center px-3 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors text-sm"
        >
          <LogOut size={16} className="mr-3" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-dvh bg-gray-50">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — drawer on mobile, static on desktop */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-200
        md:relative md:translate-x-0 md:flex md:flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {sidebarContent}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center bg-gray-900 px-4 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1 mr-3 rounded-lg text-gray-300 hover:bg-gray-800"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center">
            <ZuttleLogo variant="white" height={20} className="mr-2" />
            <span className="text-gray-300 text-sm">Admin</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default App;
